import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppSelector } from '~/hooks/useAppSelector';
import useEngagementTracking from '~/hooks/useEngagementTracking';
import usePageViewTracking from '~/hooks/usePageViewTracking';
import useUsageTracking from '~/hooks/useUsageTracking';
import { ReactFunctionComponent } from '~/types/interfaces';

const UsageTrackingProvider = ({ children }: ReactFunctionComponent) => {
  const location = useLocation();

  // Keep the existing usage tracking for idle time and activity
  useUsageTracking({
    trackPageViews: false, // Disable page views here, we'll handle it separately
    trackIdleTime: true,
    idleTimeoutMs: 300000, // 5 minutes
  });

  // Use our enhanced page view tracking with ecosystem/org info
  const { trackPageView: trackEnhancedPageView } = usePageViewTracking();

  // Use engagement tracking for active users and session engagement
  const { trackPageVisit, trackAction } = useEngagementTracking({
    trackActiveUsers: true,
    trackEngagementSessions: true,
  });

  const { user, isAuthenticated } = useAppSelector(({ auth }) => auth);

  // Track page views on route changes with ecosystem and organization info
  useEffect(() => {
    if (isAuthenticated && user) {
      const pageName = getPageNameFromPath(location.pathname);

      // Track enhanced page view with ecosystem/org info
      trackEnhancedPageView({
        page_name: pageName,
        additional_properties: {
          path: location.pathname,
          search: location.search,
        },
      });

      // Track page visit for engagement tracking
      trackPageVisit(pageName);
    }
  }, [
    location.pathname,
    location.search,
    isAuthenticated,
    user,
    trackEnhancedPageView,
    trackPageVisit,
  ]);

  // Track user actions for engagement (clicks, form submissions, etc.)
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const handleUserAction = () => {
      trackAction();
    };

    // Listen for user interactions that indicate engagement
    const actionEvents = ['click', 'submit', 'keydown'];

    actionEvents.forEach((event) => {
      document.addEventListener(event, handleUserAction, { passive: true });
    });

    return () => {
      actionEvents.forEach((event) => {
        document.removeEventListener(event, handleUserAction);
      });
    };
  }, [isAuthenticated, user, trackAction]);

  return <>{children}</>;
};

// Helper function to extract meaningful page names from paths
const getPageNameFromPath = (pathname: string): string => {
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length === 0) return 'home';

  // Map common routes to readable names
  const routeMap: Record<string, string> = {
    search: 'search',
    profile: 'profile',
    activities: 'activities',
    ecosystem: 'ecosystem',
    applications: 'applications',
    settings: 'settings',
    notifications: 'notifications',
    'in-app': 'in_app',
  };

  const firstSegment = segments[0];

  if (routeMap[firstSegment]) {
    // For nested routes, combine segments for more context
    if (segments.length > 1) {
      return `${routeMap[firstSegment]}_${segments[1]}`;
    }
    return routeMap[firstSegment];
  }

  // For unknown routes, use the first segment
  return firstSegment.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
};

export default UsageTrackingProvider;
